import React, { useMemo } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { FontAwesome6 } from '@react-native-vector-icons/fontawesome6/static';
import Ionicons from '@react-native-vector-icons/ionicons';
import MaterialCommunityIcons from '@react-native-vector-icons/material-design-icons/static';
import { GTFonts, useTheme, withOpacity, type Theme } from '../../../theme';
import { borderWidth, fs, iconSize, s, v } from '../../../utils/libs';

/**
 * Step 2: Workspace overview (US2, T043). Three scan-friendly concept cards —
 * Home, New Sale, Back Office — without listing every navigation tab.
 * New Sale / Back Office icons + colors match the Home owner CTAs; Home stays
 * the existing outline/primary treatment.
 */

type ConceptId = 'home' | 'new_sale' | 'back_office';

const CONCEPTS: Array<{
  id: ConceptId;
  title: string;
  description: string;
}> = [
  {
    id: 'home',
    title: 'Home',
    description:
      'View your business daily performance, pick your next action, and recent transactions.',
  },
  {
    id: 'new_sale',
    title: 'New Sale',
    description:
      'Find products, add to basket, and accept payments.',
  },
  {
    id: 'back_office',
    title: 'Back Office',
    description:
      'Track Cashflow, manage inventory, staff, orders, and everything behind the counter.',
  },
];

const WorkspaceOverviewStep: React.FC = () => {
  const { theme } = useTheme();
  const styles = useMemo(() => createStyles(theme), [theme]);

  return (
    <View style={styles.cardList} accessibilityRole="list">
      {CONCEPTS.map(concept => {
        const accent =
          concept.id === 'back_office'
            ? theme.colors.successEmerald
            : theme.colors.primary;

        return (
          <View
            key={concept.id}
            style={styles.card}
            accessible
            accessibilityLabel={`${concept.title}. ${concept.description}`}>
            <View
              style={[
                styles.cardIcon,
                { backgroundColor: withOpacity(accent, 0.1) },
              ]}>
              {concept.id === 'home' ? (
                <Ionicons
                  name="home-outline"
                  size={iconSize(22)}
                  color={theme.colors.primary}
                />
              ) : concept.id === 'new_sale' ? (
                <MaterialCommunityIcons
                  name="plus-circle"
                  size={iconSize(24)}
                  color={theme.colors.primary}
                />
              ) : (
                <FontAwesome6
                  iconStyle="solid"
                  name="circle-dollar-to-slot"
                  size={iconSize(22)}
                  color={theme.colors.successEmerald}
                />
              )}
            </View>
            <View style={styles.cardText}>
              <Text style={styles.cardTitle}>{concept.title}</Text>
              <Text style={styles.cardDescription}>{concept.description}</Text>
            </View>
          </View>
        );
      })}
    </View>
  );
};

const createStyles = (theme: Theme) =>
  StyleSheet.create({
    cardList: {
      gap: v(12),
    },
    card: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      backgroundColor: theme.colors.surface,
      borderRadius: theme.borderRadius.md,
      borderWidth: borderWidth(1),
      borderColor: theme.colors.borderSecondary,
      padding: s(16),
      gap: s(14),
    },
    cardIcon: {
      width: iconSize(44),
      height: iconSize(44),
      borderRadius: theme.borderRadius.md,
      alignItems: 'center',
      justifyContent: 'center',
    },
    cardText: {
      flex: 1,
      gap: v(4),
    },
    cardTitle: {
      fontFamily: GTFonts.semibold,
      fontSize: fs(16),
      lineHeight: fs(21),
      color: theme.colors.text,
    },
    cardDescription: {
      fontFamily: GTFonts.regular,
      fontSize: fs(14),
      lineHeight: fs(19),
      color: theme.colors.textSecondary,
    },
  });

export default WorkspaceOverviewStep;
